import React, { useEffect, useState } from "react";
import { Spinner, Button, Modal, Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import TaskService from "../../services/task.service";
import chatService from "../../services/chat.service";
import Default from "../../components/layouts/default";
import { useNavigate } from "react-router-dom";
import Pagination from "../../components/common/Pagination";
import AnimeSkeleton from "../../components/common/Skeleton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { FiMessageSquare } from "react-icons/fi";

const Tasks = () => {
  const [taskList, setTaskList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState({ show: false, task: null });
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [currentSet, setCurrentSet] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [showPendingOnly, setShowPendingOnly] = useState(false);
  const [sortBy, setSortBy] = useState('taskDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const navigate = useNavigate();

  const fetchTasks = async (currentPage = 1) => {
    try {
      setLoading(true);

      const status = showPendingOnly ? 'pending' : '';
      const res = await TaskService.getTaskList(currentPage, limit, searchTerm, status, sortBy, sortOrder);

      setTaskList(res.tasks);
      setPage(currentPage);
      setTotalPages(res.totalPages || 1);
    } catch (err) {
      console.error("Failed to load tasks:", err);
    } finally {
      setLoading(false);
    }
  };



  const confirmCompleteTask = async () => {
    const task = confirmModal.task;
    try {
await TaskService.updateTask(task.thread_id, {
  taskIndex: task.taskIndex,
  taskDone: true,
});
      // Post the same "<task> completed" message the chat page sends
      await chatService.SendNewMessage(task.thread_id, {
        thread_id: task.thread_id,
        messageBody: `${task.taskDetails} completed`,
      });
      fetchTasks();
    } catch (err) {
      console.error("Error updating task:", err);
    } finally {
      setConfirmModal({ show: false, task: null });
    }
  };


  useEffect(() => {
    fetchTasks(page);
  }, [page, searchTerm, showPendingOnly, sortBy, sortOrder]);


  return (
    <Default pageTitle="Tasks">
      <div className="Tasks">
        <div className=" mx-0">
          <div className="card card-body px-0">
            <div className="d-flex flex-lg-row flex-column justify-content-between align-items-center">
              <div className="documents__toolbar d-flex justify-content-between mb-3 ps-3 align-items-center">
                <div className="mx-2 me-4 d-flex align-items-center gap-2">
                  <input
                    type="search"
                    placeholder="Search"
                    value={searchInput}
                    onChange={(e) => {
                      if (e.target.value === "") {
                        setSearchTerm("");
                        setPage(1);
                      }
                      setSearchInput(e.target.value);
                    }}
                    style={{ boxShadow: "none" }}
                    className="form-control form-control-sm"
                  />
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => {
                      setSearchTerm(searchInput);
                      setPage(1);
                    }}
                  >
                    Search
                  </button>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="showPendingOnly"
                    checked={showPendingOnly}
                    onChange={(e) => {
                      setShowPendingOnly(e.target.checked);
                      setPage(1);
                    }}
                  />
                  <label className="form-check-label" htmlFor="showPendingOnly">
                    Show Pending
                  </label>
                </div>
              </div>
              <p className="task-hint text-muted small mb-3 pe-4">
                Finished a task? Click
                <span className="task-action task-action--complete task-action--inline mx-1" aria-hidden="true">
                  <FontAwesomeIcon icon={faCheck} />
                </span>
                next to it to mark it complete.
              </p>
            </div>

            <div className="table-responsive">
              <table className="table table-striped rounded-3 overflow-hidden">
                <thead style={{ backgroundColor: "#F5F6FA" }}>
                  <tr>
                    <th scope="col" className="text-nowrap" style={{ width: "1%" }}>S.No.</th>
                    <th scope="col" style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setSortOrder(sortBy === 'taskDate' ? (sortOrder === 'asc' ? 'desc' : 'asc') : 'desc');
                        setSortBy('taskDate');
                        setPage(1);
                      }}
                    >
                      Task Date {sortBy === 'taskDate' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th scope="col" width="30%" style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setSortOrder(sortBy === 'title' ? (sortOrder === 'asc' ? 'desc' : 'asc') : 'asc');
                        setSortBy('title');
                        setPage(1);
                      }}
                    >
                      Task Details {sortBy === 'title' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th scope="col">Company Name</th>
                    <th scope="col" style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setSortOrder(sortBy === 'status' ? (sortOrder === 'asc' ? 'desc' : 'asc') : 'asc');
                        setSortBy('status');
                        setPage(1);
                      }}
                    >
                      Status {sortBy === 'status' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th scope="col" style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setSortOrder(sortBy === 'date' ? (sortOrder === 'asc' ? 'desc' : 'asc') : 'asc');
                        setSortBy('date');
                        setPage(1);
                      }}
                    >
                      Completed Date {sortBy === 'date' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th scope="col">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <AnimeSkeleton rows={5} cols={7} />
                  ) : taskList.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center text-muted p-3">
                        No tasks found.
                      </td>
                    </tr>
                  ) : (
                    taskList.map((task, index) => (
                      <tr key={index}>
                        <td className="text-black text-start text-nowrap">
                          {(page - 1) * limit + index + 1}
                        </td>
                        <td className="text-black text-start text-nowrap">
                          {task.formattedTaskDate || "-"}
                        </td>
                        <td className="text-black fw-medium text-start">
                          {task.taskDetails}
                        </td>
                        <td className="text-black text-start">
                          {task.client_name || "-"}
                        </td>
                        <td className="text-start">
                          <Badge
                            pill
                            bg={task.taskDone ? "success" : "danger"}
                            className="fw-semibold"
                          >
                            {task.taskDone ? "Completed" : "Pending"}
                          </Badge>
                        </td>
                        <td className="text-black text-start">
                          {task.taskDone && task.formattedDate ? task.formattedDate : "-"}
                        </td>
                        <td className="text-start">
                          <div className="d-flex align-items-center gap-2">
                            {task.taskDone ? (
                              <OverlayTrigger placement="top" overlay={<Tooltip>Completed</Tooltip>}>
                                <span className="task-action task-action--done" aria-label="Completed">
                                  <FontAwesomeIcon icon={faCheck} />
                                </span>
                              </OverlayTrigger>
                            ) : (
                              <OverlayTrigger placement="top" overlay={<Tooltip>Mark as complete</Tooltip>}>
                                <button
                                  type="button"
                                  className="task-action task-action--complete"
                                  aria-label="Mark as complete"
                                  onClick={() => setConfirmModal({ show: true, task })}
                                >
                                  <FontAwesomeIcon icon={faCheck} />
                                </button>
                              </OverlayTrigger>
                            )}
                            <OverlayTrigger placement="top" overlay={<Tooltip>Go to chat</Tooltip>}>
                              <button
                                type="button"
                                className="task-action task-action--chat"
                                aria-label="Go to chat"
                                onClick={() =>
                                  navigate(`/chats?threadId=${task.thread_id}`, {
                                    state: { taskThreadId: task.thread_id },
                                  })
                                }
                              >
                                <FiMessageSquare />
                              </button>
                            </OverlayTrigger>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {taskList.length > 0 && (
              <div className="d-flex justify-content-end mt-3 pe-4">
                <Pagination
                  currentPage={page}
                  TotalPage={totalPages}
                  currentSet={currentSet}
                  setCurrentSet={setCurrentSet}
                  setcurrentPage={(newPage) => setPage(newPage)}
                />
              </div>
            )}

            <p className="text-muted small ps-4 mt-3 mb-0">
              All dates are in US Central Time (CT).
            </p>

            <Modal
              show={confirmModal.show}
              onHide={() => setConfirmModal({ show: false, task: null })}
            >
              <Modal.Header closeButton>
                <Modal.Title>Confirm Completion</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                Mark this task as completed?
                {confirmModal.task?.taskDetails && (
                  <div className="fw-medium text-black mt-2">{confirmModal.task.taskDetails}</div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => setConfirmModal({ show: false, task: null })}
                >
                  Cancel
                </Button>
                <Button
                  variant="success"
                  onClick={confirmCompleteTask}
                >
                  Yes, Mark Done
                </Button>
              </Modal.Footer>
            </Modal>
          </div>
        </div>
      </div>
    </Default>
  );
};

export default Tasks;
